import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MdWeekend,
  MdCollections,
  MdAutoFixHigh,
  MdChevronRight,
  MdClose,
  MdLogout,
} from 'react-icons/md';

interface NavItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  path: string;
}

const navItems: NavItem[] = [
  {
    id: 'customize',
    label: 'Customize',
    icon: <MdWeekend size={22} />,
    path: '/customize',
  },
  {
    id: 'collections',
    label: 'Collections',
    icon: <MdCollections size={22} />,
    path: '/collections',
  },
  {
    id: 'style-assistant',
    label: 'Style Assistant',
    icon: <MdAutoFixHigh size={22} />,
    path: '/style-assistant',
  },
];

const Sidebar: React.FC = () => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  const handleNavClick = (path: string) => {
    navigate(path);
  };

  return (
    <motion.div
      initial={false}
      animate={{
        width: isCollapsed ? '78px' : '260px',
      }}
      transition={{
        duration: 0.3,
        ease: [0.4, 0, 0.2, 1],
      }}
      className="relative h-screen flex flex-col z-30"
      style={{
        background: 'linear-gradient(180deg, #181818, #121212)',
        borderRight: '1px solid rgba(255, 255, 255, 0.06)',
        boxShadow: '4px 0 24px rgba(0, 0, 0, 0.5)',
      }}
    >
      {/* Logo Section */}
      <div
        className="flex items-center justify-center border-b border-white/5 overflow-hidden flex-shrink-0"
        style={{
          height: isCollapsed ? '80px' : '100px',
          padding: isCollapsed ? '16px 12px' : '20px 16px',
          transition: 'height 0.3s cubic-bezier(0.4, 0, 0.2, 1), padding 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <AnimatePresence mode="wait">
          {isCollapsed ? (
            <motion.div
              key="icon"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.2 }}
              className="flex-shrink-0 flex items-center justify-center"
            >
              <img
                src="/assets/images/genaiIcon.svg"
                alt="GenAI"
                className="w-11 h-11 object-contain"
              />
            </motion.div>
          ) : (
            <motion.div
              key="logo"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.25 }}
              className="flex items-center justify-center w-full"
            >
              <img
                src="/assets/images/genai_logo.png"
                alt="GenAI Embed"
                className="h-14 max-w-full object-contain"
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Navigation Items */}
      <div className="flex-1 px-3 py-6 space-y-1.5 overflow-hidden">
        {!isCollapsed && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-[10px] font-semibold text-gray-600 uppercase tracking-widest px-3 mb-4"
          >
            Features
          </motion.p>
        )}

        {navItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <motion.button
              key={item.id}
              onClick={() => handleNavClick(item.path)}
              whileTap={{ scale: 0.97 }}
              className={`w-full flex items-center gap-3 rounded-xl transition-all duration-200 relative group cursor-pointer ${
                isCollapsed ? 'px-0 py-3 justify-center' : 'px-3 py-3'
              }`}
              style={{
                background: isActive
                  ? 'linear-gradient(135deg, rgba(238, 68, 35, 0.12), rgba(238, 68, 35, 0.06))'
                  : 'transparent',
                border: isActive
                  ? '1px solid rgba(238, 68, 35, 0.2)'
                  : '1px solid transparent',
              }}
            >
              {/* Active indicator bar */}
              {isActive && (
                <motion.div
                  layoutId="activeIndicator"
                  className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-6 rounded-r-full bg-[#EE4423]"
                  transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                />
              )}

              <div
                className={`flex-shrink-0 transition-colors duration-200 ${
                  isActive ? 'text-[#EE4423]' : 'text-gray-500 group-hover:text-gray-300'
                }`}
              >
                {item.icon}
              </div>

              <AnimatePresence>
                {!isCollapsed && (
                  <motion.span
                    initial={{ opacity: 0, width: 0 }}
                    animate={{ opacity: 1, width: 'auto' }}
                    exit={{ opacity: 0, width: 0 }}
                    transition={{ duration: 0.2 }}
                    className={`text-sm font-medium whitespace-nowrap ${
                      isActive ? 'text-white' : 'text-gray-400 group-hover:text-gray-200'
                    }`}
                  >
                    {item.label}
                  </motion.span>
                )}
              </AnimatePresence>

              {/* Tooltip for collapsed state */}
              {isCollapsed && (
                <div className="absolute left-full ml-3 px-3 py-1.5 bg-[#212121] text-white text-xs font-medium rounded-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap z-50 border border-white/10 shadow-xl">
                  {item.label}
                </div>
              )}
            </motion.button>
          );
        })}
      </div>

      {/* Bottom Section */}
      <div className="px-3 py-4 border-t border-white/5">
        <motion.button
          whileTap={{ scale: 0.97 }}
          className={`w-full flex items-center gap-3 rounded-xl py-3 text-gray-500 hover:text-red-400 transition-colors cursor-pointer group ${
            isCollapsed ? 'px-0 justify-center' : 'px-3'
          }`}
        >
          <MdLogout size={20} className="flex-shrink-0" />
          <AnimatePresence>
            {!isCollapsed && (
              <motion.span
                initial={{ opacity: 0, width: 0 }}
                animate={{ opacity: 1, width: 'auto' }}
                exit={{ opacity: 0, width: 0 }}
                transition={{ duration: 0.2 }}
                className="text-sm font-medium whitespace-nowrap"
              >
                Logout
              </motion.span>
            )}
          </AnimatePresence>

          {/* Tooltip for collapsed */}
          {isCollapsed && (
            <div className="absolute left-full ml-3 px-3 py-1.5 bg-[#212121] text-white text-xs font-medium rounded-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap z-50 border border-white/10 shadow-xl">
              Logout
            </div>
          )}
        </motion.button>
      </div>

      {/* Toggle Button */}
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="absolute -right-3 top-[52px] cursor-pointer w-6 h-6 bg-[#EE4423] hover:bg-[#d63d20] rounded-full flex items-center justify-center transition-all duration-300 shadow-lg shadow-[#EE4423]/20 z-50"
      >
        <motion.div
          initial={false}
          animate={{ rotate: isCollapsed ? 0 : 180 }}
          transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
        >
          {isCollapsed ? (
            <MdChevronRight size={16} className="text-white" />
          ) : (
            <MdClose size={14} className="text-white" />
          )}
        </motion.div>
      </button>
    </motion.div>
  );
};

export default Sidebar;
